// @djsem/ui — First-Run, Onboarding, Demo & Safe Import model (Canonical UI Prompt 82).
//
// Pure logic that the React screens render. No IPC/filesystem calls here — the desktop app
// wires those in. The canonical safety copy in this file is authoritative.

// ---- First-Run Flow (prompt 82 §3) ----

/** A step in the first-run wizard. */
export type DJSEMFirstRunStep = "welcome" | "mode" | "org_project" | "first_action" | "checklist";
/** How the user chooses to start: sign in, try the demo, or work fully offline/local. */
export type DJSEMFirstRunMode = "sign_in" | "demo" | "offline_local";
/** The first concrete action offered at the end of first-run. */
export type DJSEMFirstAction = "import_music" | "import_rekordbox" | "import_playlist" | "load_demo" | "blank_project";

/** Canonical ordered first-run steps (prompt 82 §3). */
export const FIRST_RUN_STEPS: readonly DJSEMFirstRunStep[] = ["welcome", "mode", "org_project", "first_action", "checklist"];

/**
 * Advance state machine. Returns the next step or `null` when complete.
 * @param current - The current first-run step.
 * @returns The next step, or `null` if `current` is the last step (or unrecognised).
 */
export function nextFirstRunStep(current: DJSEMFirstRunStep): DJSEMFirstRunStep | null {
  const i = FIRST_RUN_STEPS.indexOf(current);
  return i < 0 || i === FIRST_RUN_STEPS.length - 1 ? null : FIRST_RUN_STEPS[i + 1];
}

// ---- Onboarding Checklist (prompt 82 §4) — persistent, progress-saving ----

/** Id of a single onboarding checklist item. */
export type DJSEMChecklistId =
  | "import_first_track" | "import_first_playlist" | "create_first_smart_crate"
  | "build_first_set" | "add_cue_points" | "run_ai_set_coach"
  | "create_brand_world" | "generate_first_visual" | "create_release_asset"
  | "create_gig_pack" | "create_client_preview" | "export_first_pack";

/** A single onboarding checklist item definition. */
export interface DJSEMChecklistItem {
  id: DJSEMChecklistId;
  label: string;
  module: "library" | "sets" | "create" | "gigs" | "assets";
  /** Prerequisite items — kept in the recommended user order for pacing. */
  requires?: DJSEMChecklistId[];
}

export const ONBOARDING_CHECKLIST: readonly DJSEMChecklistItem[] = [
  { id: "import_first_track", label: "Import your first track", module: "library" },
  { id: "import_first_playlist", label: "Import a playlist", module: "library", requires: ["import_first_track"] },
  { id: "create_first_smart_crate", label: "Create a smart crate", module: "library", requires: ["import_first_track"] },
  { id: "build_first_set", label: "Build a set", module: "sets", requires: ["import_first_track"] },
  { id: "add_cue_points", label: "Add cue points", module: "sets", requires: ["build_first_set"] },
  { id: "run_ai_set_coach", label: "Run the AI Set Coach", module: "sets", requires: ["build_first_set"] },
  { id: "create_brand_world", label: "Create a Brand World", module: "create" },
  { id: "generate_first_visual", label: "Generate your first visual", module: "create", requires: ["create_brand_world"] },
  { id: "create_release_asset", label: "Create a release asset", module: "create", requires: ["create_brand_world"] },
  { id: "create_gig_pack", label: "Create a gig pack", module: "gigs" },
  { id: "create_client_preview", label: "Send a client preview", module: "gigs", requires: ["create_gig_pack"] },
  { id: "export_first_pack", label: "Export your first pack", module: "assets", requires: ["create_gig_pack"] },
] as const;

/** Per-user onboarding progress: which checklist items are done. */
export interface OnboardingProgress { completed: Record<DJSEMChecklistId, boolean>; }

/** @returns A fresh {@link OnboardingProgress} with every item marked incomplete. */
export function initialProgress(): OnboardingProgress {
  return { completed: Object.fromEntries(ONBOARDING_CHECKLIST.map((i) => [i.id, false])) as OnboardingProgress["completed"] };
}

/**
 * Resolve items with availability (blocked if a prerequisite isn't done yet). Kept in canonical order.
 * @param progress - Current onboarding progress.
 * @returns The checklist items augmented with `done` and `available` flags.
 */
export function resolveChecklist(progress: OnboardingProgress): Array<DJSEMChecklistItem & { done: boolean; available: boolean }> {
  return ONBOARDING_CHECKLIST.map((item) => ({
    ...item,
    done: !!progress.completed[item.id],
    available: !item.requires || item.requires.every((r) => progress.completed[r]),
  }));
}

/**
 * Mark a checklist item complete.
 * @param progress - Current progress.
 * @param id - The item to mark complete.
 * @returns Updated progress.
 */
export function markComplete(progress: OnboardingProgress, id: DJSEMChecklistId): OnboardingProgress {
  return { completed: { ...progress.completed, [id]: true } };
}

/**
 * Overall onboarding completion percentage.
 * @param progress - Current progress.
 * @returns A rounded percent, 0..100.
 */
export function progressPct(progress: OnboardingProgress): number {
  const total = ONBOARDING_CHECKLIST.length;
  const done = ONBOARDING_CHECKLIST.filter((i) => progress.completed[i.id]).length;
  return total === 0 ? 0 : Math.round((done / total) * 100);
}

// ---- Import Wizard (prompt 82 §6) ----

/** Source of an import (prompt 82 §6). */
export type DJSEMImportSource = "local_files" | "rekordbox_xml" | "spotify" | "soundcloud";
/** A step in the import wizard flow. */
export type DJSEMImportStep = "choose_source" | "pick_input" | "preview" | "confirm" | "importing" | "done";

/** Pre-confirmation preview for an import, including the safety warnings the user must see. */
export interface ImportPreview {
  source: DJSEMImportSource;
  itemCount: number;
  metadataOnly: boolean;
  warnings: string[];
  requiresLocalFileAccess: boolean;
  writesToRekordboxLibrary: false;
}

/**
 * Build a preview + the safety warnings the user must see BEFORE confirming.
 * @param source - The import source.
 * @param itemCount - Number of items detected for import.
 * @returns The {@link ImportPreview}, including any required safety warnings.
 */
export function buildImportPreview(source: DJSEMImportSource, itemCount: number): ImportPreview {
  const warnings: string[] = [];
  const metadataOnly = source === "spotify" || source === "soundcloud";
  const requiresLocalFileAccess = source === "local_files" || source === "rekordbox_xml";
  if (metadataOnly) {
    warnings.push("This is a METADATA-ONLY import. DJSEM stores track references and analysis — the audio is NOT downloaded from the platform.");
    warnings.push("Protected platform audio cannot be played back from DJSEM. Match tracks to your own local audio for playback.");
  }
  if (source === "rekordbox_xml") {
    warnings.push("DJSEM will READ your Rekordbox XML. It will NEVER overwrite your Rekordbox library. Any export creates a new XML file.");
  }
  if (requiresLocalFileAccess) {
    warnings.push("DJSEM only reads files you explicitly select. It does not move or delete them.");
  }
  return { source, itemCount, metadataOnly, warnings, requiresLocalFileAccess, writesToRekordboxLibrary: false };
}

// ---- Local file permission copy (prompt 82 §5) — CANONICAL ----

/** Canonical local-file permission copy shown before granting DJSEM file access (prompt 82 §5). */
export const LOCAL_FILE_PERMISSION_COPY = [
  "DJSEM needs access to the files and folders you choose — nothing else.",
  "DJSEM does not move or delete your files unless you explicitly run an export or delete action.",
  "Spotify and SoundCloud imports are metadata references only.",
  "Protected platform audio is not downloaded.",
  "Rekordbox export creates a new XML/export file and does NOT overwrite your source library.",
] as const;

// ---- Demo Project (prompt 82 §7) ----

/** Summary of the bundled demo project (prompt 82 §7). */
export interface DemoProjectSummary {
  id: string;
  label: string;
  trackCount: number;
  setCount: number;
  brandWorldCount: number;
  /** Every asset in the demo is either owned or licensed — rights preflight is guaranteed to pass. */
  allRightsCleared: true;
}

/** @returns The canonical "Wedding Warmup" demo project summary. */
export function defaultDemoProject(): DemoProjectSummary {
  return { id: "demo_wedding_warmup", label: "Wedding Warmup (demo)", trackCount: 12, setCount: 2, brandWorldCount: 1, allRightsCleared: true };
}
